import { CSSProperties, useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Display } from '@cbhq/cds-common';
import { zIndex } from '@cbhq/cds-common/tokens/zIndex';
import { IconButton } from '@cbhq/cds-web/buttons';
import { Dropdown } from '@cbhq/cds-web/dropdown/Dropdown';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Box, Divider, VStack } from '@cbhq/cds-web/layout';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { PopoverContentPositionConfig } from '@cbhq/cds-web/overlays/popover/PopoverProps';
import { ActionType, AnalyticsEventImportance, ComponentType } from '@cbhq/client-analytics';

import { PACKAGE_VERSION_NAME } from '../../version';
import { useAnalytics } from '../hooks/useAnalytics';
import { useLogEventOnMount } from '../hooks/useLogEventOnMount';

import { AppSwitcherButton } from './AppSwitcherButton';
import { AppSwitcherInside } from './AppSwitcherInside';

const mobileContentWrapperStyle: CSSProperties = {
  scrollbarWidth: 'none',
};

const messages = defineMessages({
  mobileCloseA11yLabel: {
    id: 'AppSwitcherButton.mobileCloseA11yLabel',
    defaultMessage: 'Close profile menu',
  },
});

export function AppSwitcherContent() {
  const { formatMessage } = useIntl();

  useLogEventOnMount(
    'app_switcher_render',
    {
      action: ActionType.render,
      componentType: ComponentType.button,
    },
    AnalyticsEventImportance.high,
  );

  const [visible, setVisible] = useState<boolean>(false);

  const { isPhone } = useBreakpoints();

  const contentPosition = {
    placement: 'bottom-end',
    gap: 1,
  } as PopoverContentPositionConfig;

  const { logEvent } = useAnalytics();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleOpen = useCallback(() => {
    logEvent('app_switcher_open', {
      action: ActionType.click,
      componentType: ComponentType.button,
    });
  }, [logEvent]);

  const handleClose = useCallback(() => {
    logEvent('app_switcher_close', {
      action: ActionType.click,
      componentType: ComponentType.button,
    });
    buttonRef?.current?.focus();
  }, [logEvent]);

  const handlePhonePress = useCallback(() => {
    if (isPhone) {
      setVisible((prev) => !prev);
    }
  }, [isPhone]);

  const dangerouslySetStyle: CSSProperties = useMemo(
    () => ({ overflowY: 'scroll', overflowX: 'hidden' }),
    [],
  );

  const dropdownBoxProps = useMemo(
    () => ({
      maxWidth: 40,
      testID: 'AppSwitcherContent',
      display: 'inline-block' as Display,
      'data-version': PACKAGE_VERSION_NAME,
    }),
    [],
  );

  const content = useMemo(() => {
    return (
      <VStack
        testID="DropdownContent"
        width={isPhone ? '100%' : '375px'}
        borderRadius={200}
        style={dangerouslySetStyle}
      >
        <AppSwitcherInside />
      </VStack>
    );
  }, [dangerouslySetStyle, isPhone]);

  const isSafariOnMobile = useMemo(
    // Check if it's an iPhone or iPad
    () => /apple/i.test(navigator.vendor) && /iPhone|iPad/i.test(navigator.userAgent),
    [],
  );

  const modalContent = useMemo(
    () => (
      <VStack
        position="fixed"
        width="100vw"
        height="100vh"
        zIndex={zIndex.portal}
        left={0}
        top={0}
        background="bg"
        /* The Safari bar may overlap content if it's too close to the bottom edge of the screen. */
        paddingBottom={isSafariOnMobile ? 10 : 0}
      >
        <HStack width="100%" paddingY={1} paddingX={2} justifyContent="flex-end">
          <IconButton
            accessibilityLabel={formatMessage(messages.mobileCloseA11yLabel)}
            name="close"
            color="fg"
            onClick={handlePhonePress}
            transparent
          />
        </HStack>
        <Divider />
        <Box overflow="scroll" padding={0} style={mobileContentWrapperStyle}>
          {content}
        </Box>
      </VStack>
    ),
    [content, formatMessage, handlePhonePress, isSafariOnMobile],
  );

  if (isPhone) {
    return (
      <>
        <AppSwitcherButton buttonRef={buttonRef} onPress={handlePhonePress} />
        {visible ? modalContent : null}
      </>
    );
  }

  return (
    <Box {...dropdownBoxProps}>
      <Dropdown
        testID="AppSwitcherContent-Dropdown"
        enableMobileModal
        contentPosition={contentPosition}
        maxHeight={800}
        onOpenMenu={handleOpen}
        onCloseMenu={handleClose}
        content={content}
        showOverlay
      >
        <AppSwitcherButton buttonRef={buttonRef} />
      </Dropdown>
    </Box>
  );
}
